// =====================================================
// JOURNAL DETAIL MODAL WITH OUTCOME EDITING
// Phase 4: Includes Counterfactual Proof Display + Structured AI Review
// =====================================================

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { updateJournalOutcome } from '@/services/journalService';
import type { JournalEntry, OutcomeStatus } from '@/types/journal';
import CounterfactualProof from './CounterfactualProof';
import ImpulsiveTraderSection from './ImpulsiveTraderSection';
import AITradeReviewDisplay from './AITradeReviewDisplay';
import MarkdownText from '../MarkdownText';
import AnalysisVisualizations from './AnalysisVisualizations';
import { useDesignVersion } from '@/hooks/useDesignVersion';
import { supabase } from '@/integrations/supabase/client';
import { resolveTradeScreenshotUrl } from '@/utils/tradeScreenshot';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

const { FiX, FiSave, FiTrendingUp, FiTrendingDown, FiClock, FiTarget, FiAlertTriangle, FiCheck, FiCopy, FiImage, FiCpu, FiTag } = FiIcons;

interface StructuredAIReview {
  summary: string;
  strengths: string[];
  mistakes: string[];
  recommendations: string[];
  risk_score: number;
  discipline_score: number;
  emotional_control_score: number;
  sentiment_label: string;
}

interface JournalDetailModalProps {
  entry: JournalEntry;
  onClose: () => void;
  onOutcomeUpdated: () => void;
}

const JournalDetailModal: React.FC<JournalDetailModalProps> = ({ entry, onClose, onOutcomeUpdated }) => {
  const [outcomeStatus, setOutcomeStatus] = useState<OutcomeStatus>(entry.outcomeStatus);
  const [outcomeRMultiple, setOutcomeRMultiple] = useState<string>(
    entry.outcomeRMultiple !== null ? entry.outcomeRMultiple.toString() : ''
  );
  const [outcomeNotes, setOutcomeNotes] = useState<string>(entry.outcomeNotes || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isGeneratingReview, setIsGeneratingReview] = useState(false);
  const [structuredReview, setStructuredReview] = useState<StructuredAIReview | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  // Bubbled-up price snapshot from CounterfactualProof so AnalysisVisualizations
  // can render the price-journey sparkline without re-fetching.
  const [priceSnapshot, setPriceSnapshot] = useState<{ priceAtDecision: number; currentPrice: number } | null>(null);
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [screenshotSrc, setScreenshotSrc] = useState<string | null>(null);
  // Multi-chart gallery — resolved signed URLs (one per uploaded chart path)
  const [screenshotSrcs, setScreenshotSrcs] = useState<string[]>([]);
  const { flags } = useFeatureFlags();
  const { version: designVersion } = useDesignVersion();
  const isV3 = designVersion === 'v3';

  // The trade-screenshots bucket is private; resolve a short-lived signed URL
  // as soon as the modal opens (was gated behind a 'View Screenshot' button
  // click before — users kept missing the button entirely).
  useEffect(() => {
    let active = true;
    // Prefer the multi-chart array; fall back to single-chart legacy field.
    const paths: string[] = Array.isArray(entry.screenshotUrls) && entry.screenshotUrls.length > 0
      ? entry.screenshotUrls
      : (entry.screenshotUrl ? [entry.screenshotUrl] : []);
    if (paths.length === 0) return;

    // Resolve all signed URLs in parallel. Filter out failed ones.
    Promise.all(paths.map((p) => resolveTradeScreenshotUrl(p)))
      .then((urls) => {
        if (!active) return;
        const validUrls = urls.filter((u): u is string => !!u);
        setScreenshotSrcs(validUrls);
        setScreenshotSrc(validUrls[0] ?? null); // legacy compat
      });
    return () => { active = false; };
  }, [entry.screenshotUrl, entry.screenshotUrls, entry.id]);

  // Load existing structured review from entry on mount
  useEffect(() => {
    // Try to parse existing review data from the entry
    const loadExistingReview = async () => {
      try {
        const { data } = await supabase
          .from('journal_entries')
          .select('ai_review_raw, ai_review_summary, ai_review_strengths, ai_review_mistakes, ai_review_recommendations, ai_risk_score, ai_discipline_score, ai_emotional_score, ai_sentiment_label')
          .eq('id', entry.id)
          .single();
        
        if (data?.ai_review_raw) {
          try {
            const parsed = JSON.parse(data.ai_review_raw);
            setStructuredReview(parsed);
          } catch {
            // If raw can't be parsed, try to construct from individual fields
            if (data.ai_review_summary) {
              setStructuredReview({
                summary: data.ai_review_summary,
                strengths: data.ai_review_strengths ? JSON.parse(data.ai_review_strengths) : [],
                mistakes: data.ai_review_mistakes ? JSON.parse(data.ai_review_mistakes) : [],
                recommendations: data.ai_review_recommendations ? JSON.parse(data.ai_review_recommendations) : [],
                risk_score: data.ai_risk_score || 5,
                discipline_score: data.ai_discipline_score || 5,
                emotional_control_score: data.ai_emotional_score || 5,
                sentiment_label: data.ai_sentiment_label || 'Calm',
              });
            }
          }
        }
      } catch (err) {
        console.error('Error loading existing review:', err);
      }
    };
    
    loadExistingReview();
  }, [entry.id]);

  const handleCopyAnalysis = async () => {
    if (entry.rawAnalysisText) {
      await navigator.clipboard.writeText(entry.rawAnalysisText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Check if trade is eligible for AI Review
  const isEligibleForAIReview = (): boolean => {
    // Must be a TRADE decision (not WAIT, NO_TRADE, etc.)
    const permission = entry.aiDecision?.executionPermission?.toUpperCase() || entry.executionPermission?.toUpperCase();
    const validPermissions = ['TRADE', 'TRADE_AUTHORIZED', 'APPROVED'];
    const hasTradePermission = validPermissions.some(p => permission?.includes(p));
    
    if (!hasTradePermission) return false;
    
    // Must have entry price and stop loss
    const hasEntry = entry.aiDecision?.entry != null || entry.entryPrice != null;
    const hasStopLoss = entry.aiDecision?.stopLoss != null || entry.stopLoss != null;
    const hasTakeProfit = entry.aiDecision?.takeProfit1 != null || entry.takeProfit1 != null;
    
    if (!hasEntry || !hasStopLoss || !hasTakeProfit) return false;
    
    // Outcome must be valid (not MISSED or CANCELLED)
    const invalidOutcomes = ['MISSED', 'CANCELLED'];
    if (invalidOutcomes.includes(entry.outcomeStatus?.toUpperCase())) return false;
    
    return true;
  };

  const canGenerateReview = isEligibleForAIReview();

  const handleGenerateAIReview = async () => {
    if (!canGenerateReview) {
      setReviewError('AI Review is only available for executed trades with entry, stop loss, and take profit.');
      return;
    }
    
    setIsGeneratingReview(true);
    setReviewError(null);
    try {
      const { data, error } = await supabase.functions.invoke('ai-trade-review', {
        body: { tradeId: entry.id }
      });

      if (error) {
        console.error('Edge function error:', error);
        setReviewError(error.message || 'Failed to connect to AI service. Please try again.');
        return;
      }

      if (data?.error) {
        console.error('API error:', data.error);
        setReviewError(data.error);
        return;
      }

      if (data?.success && data?.review) {
        setStructuredReview(data.review);
        setReviewError(null);
      } else {
        setReviewError('Unexpected response from AI service. Please try again.');
      }
    } catch (err: any) {
      console.error('Error generating AI review:', err);
      setReviewError(err.message || 'Could not contact AI service. Please try again later.');
    } finally {
      setIsGeneratingReview(false);
    }
  };

  const handleSaveOutcome = async () => {
    console.log('📝 Saving outcome for entry:', entry.id);
    console.log('📝 Entry user_id:', entry.userId);
    console.log('📝 New status:', outcomeStatus);
    setIsSaving(true);
    try {
      const rMultiple = outcomeRMultiple.trim() !== '' ? parseFloat(outcomeRMultiple) : null;
      
      if (outcomeRMultiple.trim() !== '' && isNaN(rMultiple!)) {
        alert('Please enter a valid number for Net Profit');
        setIsSaving(false);
        return;
      }

      console.log('📝 Calling updateJournalOutcome with:', {
        id: entry.id,
        outcomeStatus,
        outcomeRMultiple: rMultiple,
        outcomeNotes: outcomeNotes.trim() || null,
      });

      const success = await updateJournalOutcome(entry.id, {
        outcomeStatus,
        outcomeRMultiple: rMultiple,
        outcomeNotes: outcomeNotes.trim() || null,
      });

      console.log('📝 Update result:', success);

      if (success) {
        setShowSuccess(true);
        setTimeout(() => {
          onOutcomeUpdated();
        }, 1000);
      } else {
        alert('Failed to save outcome. Check console for details.');
      }
    } catch (error) {
      console.error('❌ Error saving outcome:', error);
      alert('Error saving outcome: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format price
  const formatPrice = (price: number | null) => {
    if (price === null) return '—';
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  };

  const { aiDecision } = entry;

  // Debug: Log entry data to verify Trade Outcome section should render
  console.log('📋 JournalDetailModal entry:', {
    id: entry.id,
    userId: entry.userId,
    assetSymbol: entry.assetSymbol,
    executionPermission: aiDecision?.executionPermission,
    outcomeStatus: entry.outcomeStatus,
    shouldShowOutcomeSection: aiDecision?.executionPermission === 'TRADE' || 
                               aiDecision?.executionPermission === 'TRADE_APPROVED' ||
                               aiDecision?.executionPermission === 'CONDITIONAL'
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-3xl max-h-[90vh] bg-gray-900 rounded-2xl border border-purple-500/30 overflow-hidden shadow-2xl shadow-purple-500/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header — market + session badges auto-detected from SYMBOL so a
              BTCUSD trade saved with stale market_type='gold' doesn't show
              'GOLD CLOSED' badges on a crypto asset. Symbol = source of truth. */}
          {(() => {
            const upSym = (entry.assetSymbol || '').toUpperCase();
            const CRYPTO_BASES = ['BTC','ETH','SOL','XRP','ADA','DOGE','AVAX','LINK','MATIC','DOT'];
            const INDICES = ['US30','NAS100','SPP500','GER30'];
            let detectedMarket = (entry.marketType || '').toLowerCase();
            if (upSym.startsWith('XAU')) detectedMarket = 'gold';
            else if (CRYPTO_BASES.some((b) => upSym.includes(b))) detectedMarket = 'crypto';
            else if (INDICES.includes(upSym)) detectedMarket = 'indices';
            // Session derivation — crypto is always 24/7
            let displaySession: string | null = entry.session || null;
            if (detectedMarket === 'crypto') displaySession = '24/7';
            return (
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xl font-bold text-white">{entry.assetSymbol}</span>
                    <span className="px-2 py-0.5 rounded-md border border-cyan-400/30 bg-cyan-500/10 text-cyan-300 text-[10px] font-mono uppercase tracking-[0.15em] font-bold">
                      {detectedMarket}
                    </span>
                    {displaySession && (
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[10px] font-mono uppercase tracking-[0.15em] ${
                        displaySession === '24/7' || (displaySession && !displaySession.toUpperCase().includes('CLOSED'))
                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                          : 'bg-gray-500/10 border-white/10 text-gray-400'
                      }`}>
                        {(displaySession === '24/7' || !displaySession.toUpperCase().includes('CLOSED')) && (
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        )}
                        {displaySession}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{formatDate(entry.createdAt)}</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
            );
          })()}

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="p-6 space-y-6">
              
              {/* EXECUTION_DECISION_BLOCK - Phase 2.2 Institutional Format */}
              <div className={`rounded-xl overflow-hidden border ${
                aiDecision.executionPermission === 'TRADE' || aiDecision.executionPermission === 'TRADE_APPROVED'
                  ? 'border-emerald-500/30' 
                  : aiDecision.executionPermission === 'NO_TRADE'
                    ? 'border-red-500/30'
                    : aiDecision.executionPermission === 'WAIT'
                      ? 'border-blue-500/30'
                      : 'border-amber-500/30'
              }`}>
                {/* Decision Header — V3 uses viral $1B redesign (big mono verdict + glow +
                    SVG confidence ring). V1 uses the original compact header so users
                    who toggle back to V1 see the pre-viral design with no v3 leak. */}
                {isV3 ? (
                  (() => {
                    const perm = aiDecision.executionPermission;
                    const isTrade = perm === 'TRADE' || perm === 'TRADE_APPROVED';
                    const tone = isTrade ? 'emerald' :
                                 perm === 'NO_TRADE' ? 'purple' :
                                 perm === 'WAIT' ? 'amber' : 'cyan';
                    const toneHex = tone === 'emerald' ? '#10b981' :
                                    tone === 'purple'  ? '#a855f7' :
                                    tone === 'amber'   ? '#f59e0b' : '#22d3ee';
                    const eyebrow = isTrade ? '▸ ai risk officer · authorized'
                                  : perm === 'NO_TRADE' ? '▸ ai risk officer · capital preservation'
                                  : perm === 'WAIT'     ? '▸ ai risk officer · strategic patience'
                                  :                       '▸ ai risk officer · conditional';
                    const bigVerdict = isTrade ? 'TRADE'
                                     : perm === 'NO_TRADE' ? 'NO-TRADE'
                                     : perm === 'WAIT'     ? 'WAIT'
                                     :                       'CONDITIONAL';
                    const conf = aiDecision.confidence;
                    const ringDash = typeof conf === 'number' ? (Math.max(0, Math.min(100, conf)) / 100) * 276.46 : 0;
                    return (
                      <div className="relative p-6 overflow-hidden"
                        style={{ background: `linear-gradient(180deg, ${toneHex}1A 0%, transparent 100%)` }}>
                        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl pointer-events-none"
                          style={{ background: `${toneHex}26` }} />
                        <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full blur-3xl pointer-events-none"
                          style={{ background: `${toneHex}1A` }} />
                        <div className="relative flex items-center justify-between gap-4 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2" style={{ color: toneHex }}>
                              {eyebrow}
                            </div>
                            <div className="font-black font-mono leading-none tracking-tight text-4xl sm:text-5xl"
                              style={{ color: toneHex, textShadow: `0 4px 30px ${toneHex}40` }}>
                              {bigVerdict}
                            </div>
                            {aiDecision.direction && aiDecision.direction !== 'NONE' && (
                              <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-mono uppercase tracking-[0.15em]"
                                style={{
                                  borderColor: aiDecision.direction === 'LONG' ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)',
                                  background:  aiDecision.direction === 'LONG' ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
                                  color:       aiDecision.direction === 'LONG' ? '#6ee7b7' : '#fca5a5',
                                }}>
                                <SafeIcon icon={aiDecision.direction === 'LONG' ? FiTrendingUp : FiTrendingDown} className="text-xs" />
                                {aiDecision.direction}
                              </div>
                            )}
                          </div>
                          {typeof conf === 'number' && (
                            <div className="relative w-24 h-24 flex-shrink-0">
                              <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
                                <circle cx="50" cy="50" r="44" fill="none" stroke={toneHex} strokeWidth="6"
                                  strokeLinecap="round" strokeDasharray={`${ringDash} 276.46`} />
                              </svg>
                              <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-2xl font-black font-mono tabular-nums" style={{ color: toneHex }}>{conf}</span>
                                <span className="text-[9px] font-mono uppercase tracking-wider text-gray-500 -mt-0.5">% conf</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  /* V1 — original compact header (pre-viral design) */
                  <div className={`p-4 ${
                    aiDecision.executionPermission === 'TRADE' || aiDecision.executionPermission === 'TRADE_APPROVED'
                      ? 'bg-emerald-500/10'
                      : aiDecision.executionPermission === 'NO_TRADE' ? 'bg-red-500/10'
                      : aiDecision.executionPermission === 'WAIT'     ? 'bg-blue-500/10'
                      : 'bg-amber-500/10'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <SafeIcon
                          icon={aiDecision.executionPermission === 'TRADE' || aiDecision.executionPermission === 'TRADE_APPROVED' ? FiCheck : FiAlertTriangle}
                          className={`w-6 h-6 ${
                            aiDecision.executionPermission === 'TRADE' || aiDecision.executionPermission === 'TRADE_APPROVED' ? 'text-emerald-400' :
                            aiDecision.executionPermission === 'NO_TRADE' ? 'text-red-400' :
                            aiDecision.executionPermission === 'WAIT' ? 'text-blue-400' : 'text-amber-400'
                          }`}
                        />
                        <div>
                          <span className={`text-lg font-bold ${
                            aiDecision.executionPermission === 'TRADE' || aiDecision.executionPermission === 'TRADE_APPROVED' ? 'text-emerald-400' :
                            aiDecision.executionPermission === 'NO_TRADE' ? 'text-red-400' :
                            aiDecision.executionPermission === 'WAIT' ? 'text-blue-400' : 'text-amber-400'
                          }`}>
                            {aiDecision.executionPermission === 'TRADE' || aiDecision.executionPermission === 'TRADE_APPROVED' ? 'EXECUTION AUTHORIZED' :
                             aiDecision.executionPermission === 'NO_TRADE' ? 'CAPITAL PRESERVATION DECISION' :
                             aiDecision.executionPermission === 'WAIT' ? 'STRATEGIC PATIENCE REQUIRED' : 'CONDITIONAL AUTHORIZATION'}
                          </span>
                          {aiDecision.direction && aiDecision.direction !== 'NONE' && (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <SafeIcon
                                icon={aiDecision.direction === 'LONG' ? FiTrendingUp : FiTrendingDown}
                                className={`w-4 h-4 ${aiDecision.direction === 'LONG' ? 'text-emerald-400' : 'text-red-400'}`}
                              />
                              <span className={`text-sm font-medium ${aiDecision.direction === 'LONG' ? 'text-emerald-400' : 'text-red-400'}`}>
                                {aiDecision.direction}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      {aiDecision.confidence !== null && (
                        <div className="text-right">
                          <div className="text-2xl font-bold text-cyan-400">{aiDecision.confidence}%</div>
                          <div className="text-xs text-gray-500">Confidence</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Structured Justification Block - Phase 2.2 */}
                {(aiDecision.marketRegime || aiDecision.primaryReason || aiDecision.invalidationLevel || aiDecision.reviewTrigger) && (
                  <div className="p-4 bg-gray-800/30 space-y-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Decision Justification
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      {aiDecision.marketRegime && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 min-w-[100px]">Market Regime:</span>
                          <span className={`font-medium ${
                            aiDecision.marketRegime === 'Risk-Off' ? 'text-red-400' :
                            aiDecision.marketRegime === 'Trend' ? 'text-emerald-400' :
                            'text-white'
                          }`}>{aiDecision.marketRegime}</span>
                        </div>
                      )}
                      {aiDecision.primaryReason && (
                        <div className="flex items-start gap-2 sm:col-span-2">
                          <span className="text-gray-500 min-w-[100px]">Primary Reason:</span>
                          <span className="text-white">{aiDecision.primaryReason}</span>
                        </div>
                      )}
                      {aiDecision.invalidationLevel && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 min-w-[100px]">Invalidation:</span>
                          <span className="text-amber-400 font-mono">{aiDecision.invalidationLevel}</span>
                        </div>
                      )}
                      {aiDecision.reviewTrigger && (
                        <div className="flex items-start gap-2 sm:col-span-2">
                          <span className="text-gray-500 min-w-[100px]">Review When:</span>
                          <span className="text-cyan-400">{aiDecision.reviewTrigger}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Phase 3: Institutional Review Block - Risk Committee (gated: Institutional AI only) */}
                {flags.hasInstitutionalReviewBlock && (aiDecision.riskOfficerStance || typeof aiDecision.capitalProtectionScore === 'number' || aiDecision.restraintReason) && (
                  <div className="p-4 bg-gradient-to-r from-purple-500/5 to-blue-500/5 border-t border-white/5">
                    <div className="flex items-center gap-2 mb-3">
                      <SafeIcon icon={FiTarget} className="w-4 h-4 text-purple-400" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Institutional Review
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      {aiDecision.riskOfficerStance && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Risk Officer:</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            aiDecision.riskOfficerStance === 'APPROVE' ? 'bg-emerald-500/20 text-emerald-400' :
                            aiDecision.riskOfficerStance === 'VETO' ? 'bg-red-500/20 text-red-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {aiDecision.riskOfficerStance === 'APPROVE' ? '✓ APPROVE' :
                             aiDecision.riskOfficerStance === 'VETO' ? '✗ VETO' : '⚠ CAUTION'}
                          </span>
                        </div>
                      )}
                      {typeof aiDecision.capitalProtectionScore === 'number' && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Capital Protection:</span>
                          <span className={`font-bold ${
                            aiDecision.capitalProtectionScore >= 70 ? 'text-emerald-400' :
                            aiDecision.capitalProtectionScore >= 50 ? 'text-amber-400' : 'text-red-400'
                          }`}>{aiDecision.capitalProtectionScore}/100</span>
                        </div>
                      )}
                      {aiDecision.disciplineAssessment && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Discipline:</span>
                          <span className={`font-medium ${
                            aiDecision.disciplineAssessment === 'Excellent' ? 'text-emerald-400' :
                            aiDecision.disciplineAssessment === 'Good' ? 'text-cyan-400' :
                            aiDecision.disciplineAssessment === 'Acceptable' ? 'text-gray-300' :
                            aiDecision.disciplineAssessment === 'Warning' ? 'text-amber-400' : 'text-red-400'
                          }`}>{aiDecision.disciplineAssessment}</span>
                        </div>
                      )}
                      {aiDecision.marketAlignment && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Market Alignment:</span>
                          <span className={`font-medium ${
                            aiDecision.marketAlignment === 'Aligned' ? 'text-emerald-400' :
                            aiDecision.marketAlignment === 'Misaligned' ? 'text-red-400' : 'text-gray-300'
                          }`}>{aiDecision.marketAlignment}</span>
                        </div>
                      )}
                      {aiDecision.restraintReason && (
                        <div className="flex items-start gap-2 sm:col-span-2">
                          <span className="text-gray-500">Restraint Reason:</span>
                          <span className="text-purple-300 italic">{aiDecision.restraintReason}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Trade Levels */}
              {(aiDecision.executionPermission === 'TRADE' || aiDecision.executionPermission === 'TRADE_APPROVED') && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-800/50 rounded-xl border border-white/5">
                    <div className="text-xs text-gray-500 mb-1">Entry Price</div>
                    <div className="text-lg font-bold text-white tabular-nums">${formatPrice(aiDecision.entry)}</div>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-xl border border-white/5">
                    <div className="text-xs text-gray-500 mb-1">Stop Loss</div>
                    <div className="text-lg font-bold text-red-400 tabular-nums">${formatPrice(aiDecision.stopLoss)}</div>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-xl border border-white/5">
                    <div className="text-xs text-gray-500 mb-1">Risk:Reward</div>
                    <div className="text-lg font-bold text-purple-400">
                      {aiDecision.riskReward ? `1:${aiDecision.riskReward}` : '—'}
                    </div>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-xl border border-white/5">
                    <div className="text-xs text-gray-500 mb-1">Take Profit 1</div>
                    <div className="text-lg font-bold text-emerald-400 tabular-nums">${formatPrice(aiDecision.takeProfit1)}</div>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-xl border border-white/5">
                    <div className="text-xs text-gray-500 mb-1">Take Profit 2</div>
                    <div className="text-lg font-bold text-emerald-400 tabular-nums">${formatPrice(aiDecision.takeProfit2)}</div>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-xl border border-white/5">
                    <div className="text-xs text-gray-500 mb-1">Take Profit 3</div>
                    <div className="text-lg font-bold text-emerald-400 tabular-nums">${formatPrice(aiDecision.takeProfit3)}</div>
                  </div>
                </div>
              )}

              {/* Phase 4: Counterfactual Proof - For NO_TRADE/WAIT decisions */}
              {(aiDecision.executionPermission === 'NO_TRADE' || aiDecision.executionPermission === 'WAIT') && (
                <>
                  <CounterfactualProof
                    journalEntryId={entry.id}
                    assetSymbol={entry.assetSymbol}
                    marketType={entry.marketType}
                    onPriceUpdate={setPriceSnapshot}
                  />
                  {/* Phase 5: Impulsive Trader Benchmark Section */}
                  <ImpulsiveTraderSection journalEntryId={entry.id} />
                </>
              )}

              {/* Metadata */}
              <div className="flex flex-wrap gap-4 text-sm">
                {entry.inputPrice && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <SafeIcon icon={FiTarget} className="w-4 h-4" />
                    Input Price: ${formatPrice(entry.inputPrice)}
                  </div>
                )}
                {entry.metadata.chartCount > 0 && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <span>{entry.metadata.chartCount} chart(s) analyzed</span>
                  </div>
                )}
              </div>

              {/* Outcome Section - Show for actual trades (TRADE, TRADE_APPROVED, CONDITIONAL) */}
              {(aiDecision?.executionPermission === 'TRADE' || 
                aiDecision?.executionPermission === 'TRADE_APPROVED' ||
                aiDecision?.executionPermission === 'CONDITIONAL') && (
                <div className="p-4 bg-gradient-to-br from-purple-500/5 to-cyan-500/5 rounded-xl border border-purple-500/10">
                  <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiTarget} className="w-4 h-4 text-purple-400" />
                    Trade Outcome
                  </h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Outcome Status */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1.5">Status</label>
                      <select
                        value={outcomeStatus}
                        onChange={(e) => setOutcomeStatus(e.target.value as OutcomeStatus)}
                        className="w-full bg-gray-800 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                      >
                        <option value="PENDING">⏳ Pending</option>
                        <option value="WIN">✅ Win</option>
                        <option value="LOSS">❌ Loss</option>
                        <option value="BREAKEVEN">➖ Breakeven</option>
                        <option value="MISSED">🚫 Missed</option>
                        <option value="CANCELLED">⛔ Cancelled</option>
                      </select>
                    </div>

                    {/* Net Profit / Net Loss */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1.5">Net Profit / Net Loss</label>
                      <input
                        type="number"
                        step="0.01"
                        value={outcomeRMultiple}
                        onChange={(e) => setOutcomeRMultiple(e.target.value)}
                        placeholder="e.g. 500 or -200"
                        className="w-full bg-gray-800 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                    </div>

                    {/* Save Button */}
                    <div className="flex items-end">
                      <button
                        onClick={handleSaveOutcome}
                        disabled={isSaving || showSuccess}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                          showSuccess
                            ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
                            : 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 shadow-lg shadow-purple-500/20'
                        }`}
                      >
                        <SafeIcon icon={showSuccess ? FiCheck : FiSave} className="w-4 h-4" />
                        {isSaving ? 'Saving...' : showSuccess ? 'Saved!' : 'Save Outcome'}
                      </button>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="mt-4">
                    <label className="block text-xs text-gray-500 mb-1.5">Notes</label>
                    <textarea
                      value={outcomeNotes}
                      onChange={(e) => setOutcomeNotes(e.target.value)}
                      placeholder="Add notes about this trade..."
                      rows={3}
                      className="w-full bg-gray-800 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                    />
                  </div>
                </div>
              )}

              {/* Viral $1B visualizations — V3 ONLY. Probability bars + execution
                  cards + price journey. Renders only if widget has data. */}
              {isV3 && (
                <AnalysisVisualizations
                  analysisText={entry.rawAnalysisText}
                  aiDecision={aiDecision}
                  priceAtDecision={priceSnapshot?.priceAtDecision ?? null}
                  currentPrice={priceSnapshot?.currentPrice ?? null}
                />
              )}

              {/* Screenshot Display — MOVED HERE (was after Full Analysis, but the
                  Full Analysis has its own inner scroll and users kept getting
                  trapped inside it, never scrolling past to see the chart). Now
                  the chart is visible immediately after the visualizations. */}
              {(entry.screenshotUrl || (entry.screenshotUrls && entry.screenshotUrls.length > 0)) && (
                <div className="rounded-xl border border-white/10 bg-gray-900/60 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                    <div>
                      <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">
                        ▸ trade screenshot{screenshotSrcs.length > 1 ? `s · ${screenshotSrcs.length}` : ''}
                      </span>
                      <h4 className="text-sm font-bold text-white">
                        {screenshotSrcs.length > 1 ? 'Chart attachments' : 'Chart attachment'}
                      </h4>
                    </div>
                  </div>
                  {screenshotSrcs.length > 0 ? (
                    screenshotSrcs.length === 1 ? (
                      // Single chart — hero layout
                      <a href={screenshotSrcs[0]} target="_blank" rel="noopener noreferrer" className="block cursor-zoom-in">
                        <img
                          src={screenshotSrcs[0]}
                          alt="Trade screenshot"
                          className="w-full max-h-[500px] object-contain bg-black/40"
                        />
                      </a>
                    ) : (
                      // Multi-chart — responsive grid gallery
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 p-2 bg-black/40">
                        {screenshotSrcs.map((src, i) => (
                          <a
                            key={i}
                            href={src}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="relative block cursor-zoom-in rounded-lg overflow-hidden border border-white/5 hover:border-cyan-400/40 transition group"
                          >
                            <img
                              src={src}
                              alt={`Chart ${i + 1}/${screenshotSrcs.length}`}
                              className="w-full h-40 object-cover"
                            />
                            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-md bg-black/70 backdrop-blur-sm text-[10px] font-mono text-cyan-300">
                              {i + 1}/{screenshotSrcs.length}
                            </div>
                            <div className="absolute inset-0 bg-cyan-500/0 group-hover:bg-cyan-500/10 transition flex items-center justify-center">
                              <SafeIcon icon={FiImage} className="text-2xl text-white opacity-0 group-hover:opacity-90 transition" />
                            </div>
                          </a>
                        ))}
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-center py-16 text-sm text-gray-500 gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-cyan-500 border-t-transparent" />
                      Loading screenshot{(entry.screenshotUrls?.length ?? 0) > 1 ? 's' : ''}…
                    </div>
                  )}
                </div>
              )}

              {/* Full Analysis — V3 renders the parsed markdown report. V1 renders the
                  raw pre-formatted text (pre-viral design). */}
              {entry.rawAnalysisText && (isV3 ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">▸ ai output</span>
                      <h4 className="text-sm font-bold text-white">Full Analysis</h4>
                    </div>
                    <button
                      onClick={handleCopyAnalysis}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-[0.15em] transition-all ${
                        copied
                          ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-300'
                          : 'border border-white/10 text-gray-300 hover:border-cyan-400/40 hover:text-cyan-300 hover:bg-cyan-500/5'
                      }`}
                    >
                      <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
                      {copied ? 'copied' : 'copy'}
                    </button>
                  </div>
                  <div className="p-5 bg-gray-900/60 rounded-xl border border-white/10 max-h-[400px] overflow-y-auto">
                    <MarkdownText text={entry.rawAnalysisText} />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-white">Full Analysis</h4>
                    <button
                      onClick={handleCopyAnalysis}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        copied
                          ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
                          : 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400'
                      }`}
                    >
                      <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
                      {copied ? 'Copied!' : 'Copy Analysis'}
                    </button>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-xl border border-white/5 max-h-64 overflow-y-auto">
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">
                      {entry.rawAnalysisText}
                    </pre>
                  </div>
                </div>
              ))}

              {/* Tags Display */}
              {(entry.sessionTag || entry.planTag || entry.strategyTag) && (
                <div className="flex flex-wrap gap-2">
                  {entry.sessionTag && (
                    <span className="px-2.5 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs font-medium flex items-center gap-1">
                      <SafeIcon icon={FiTag} className="w-3 h-3" />
                      {entry.sessionTag}
                    </span>
                  )}
                  {entry.planTag && (
                    <span className="px-2.5 py-1 rounded-full bg-purple-500/20 text-purple-400 text-xs font-medium flex items-center gap-1">
                      <SafeIcon icon={FiTag} className="w-3 h-3" />
                      {entry.planTag}
                    </span>
                  )}
                  {entry.strategyTag && (
                    <span className="px-2.5 py-1 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-medium flex items-center gap-1">
                      <SafeIcon icon={FiTag} className="w-3 h-3" />
                      {entry.strategyTag}
                    </span>
                  )}
                </div>
              )}

              {/* AI Review Section */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/5 to-cyan-500/5 border border-purple-500/20">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                    <SafeIcon icon={FiCpu} className="w-4 h-4 text-purple-400" />
                    AI Trade Review
                  </h4>
                  {canGenerateReview && (
                    <button
                      onClick={handleGenerateAIReview}
                      disabled={isGeneratingReview}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-900/80 border border-purple-500/30 hover:border-cyan-500/50 text-xs font-medium transition-all disabled:opacity-50"
                    >
                      {isGeneratingReview ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-2 border-cyan-400 border-t-transparent" />
                          <span className="text-gray-300">Generating...</span>
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiCpu} className="w-3 h-3 text-cyan-400" />
                          <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                            {structuredReview ? 'Refresh Review' : 'Generate AI Review'}
                          </span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                {/* Not Eligible Message */}
                {!canGenerateReview && !structuredReview && (
                  <div className="p-3 rounded-lg bg-gray-800/50 border border-gray-700/50">
                    <p className="text-gray-500 text-sm flex items-center gap-2">
                      <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-gray-600" />
                      AI Review is only available for executed trades with entry, stop loss, and take profit.
                    </p>
                  </div>
                )}
                
                {/* Error Display */}
                {reviewError && (
                  <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                    <p className="text-red-400 text-sm flex items-center gap-2">
                      <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
                      {reviewError}
                    </p>
                  </div>
                )}
                
                {/* Review Content */}
                {(canGenerateReview || structuredReview) && (
                  <AITradeReviewDisplay review={structuredReview} isLoading={isGeneratingReview} />
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JournalDetailModal;
